import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  PenLine,
  X,
  Sparkles,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const DISMISSED_KEY = "bolso_onboarding_dismissed";

interface OnboardingBannerProps {
  onStartFirstTransaction?: () => void;
}

export function OnboardingBanner({ onStartFirstTransaction }: OnboardingBannerProps) {
  const navigate = useNavigate();
  const { ui, t } = useI18n();
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setDismissed(true);
  };

  return (
    <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />

      <CardContent className="p-5">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          aria-label={t.common.close}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-primary/10 rounded-lg shrink-0">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground">{ui.onboarding.title}</h3>
              <Badge variant="secondary" className="text-xs">{ui.onboarding.badge}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {ui.onboarding.description}
            </p>
          </div>
        </div>

        <div className="mb-4 rounded-lg bg-background/60 p-3">
          <div className="flex items-start gap-2">
            <PenLine className="h-4 w-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-foreground">{ui.onboarding.manualTitle}</p>
              <p className="text-xs text-muted-foreground">{ui.onboarding.manualDescription}</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            onClick={onStartFirstTransaction ?? (() => navigate("/transactions"))}
            className="gap-1.5"
          >
            <PenLine className="h-4 w-4" />
            {ui.onboarding.primaryAction}
          </Button>
          <button
            onClick={handleDismiss}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
          >
            {ui.onboarding.dismiss}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
